/**
 * heroStateEngine.js — HeroBrain 1차 구현 (순수 함수, 화면 미연결)
 *
 * 목적: 홈 히어로 / 셔틀 히어로가 각자 만들던 ad-hoc 분기를
 * intent × timeContext × dataConfidence 3축 모델로 정리하기 위한 엔진.
 *
 * 이 파일은 어떤 화면에도 import 되지 않는다 (의도적). 기존 App.jsx의
 * getHomeHeroState() 등은 그대로 두고, 같은 입력을 넣었을 때 결과를
 * 비교해볼 수 있도록 독립적으로 존재한다.
 *
 * 제약:
 *   - React/hook 사용 금지, localStorage/sessionStorage 사용 금지
 *   - Date/입력 객체를 변형(mutate)하지 않음 — 모두 읽기만 함
 *   - hysteresis(이전 상태 비교/디바운스)는 2차 구현에서 다룸 — 여기선
 *     매 호출마다 입력만으로 결정되는 raw 계산만 수행한다
 */

import { PERIOD_TIMES } from '../data/periodTimes'
import {
  formatRelativeTime,
  resolveClassTimingState,
  HOME_CLASS_IMMINENT_MAX_MINUTES,
  HOME_CLASS_COUNTDOWN_MAX_MINUTES,
  HOME_CLASS_TIMING_STATES,
} from './homeTimePolicy'

// ── 시간 변환 헬퍼 (순수) ────────────────────────────────────────────────────

function timeStrToMin(value) {
  if (!value) return null
  const [h, m] = String(value).split(':').map(Number)
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null
}

function getPeriodStartMin(period) {
  return timeStrToMin(PERIOD_TIMES[period]?.start)
}

function getPeriodEndMin(period) {
  return timeStrToMin(PERIOD_TIMES[period]?.end)
}

function formatClockFromMin(minutes) {
  if (minutes == null || !Number.isFinite(minutes)) return null
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}

function getClassTimeLabel(course) {
  if (!course) return null
  if (Number.isFinite(course.startMin) && Number.isFinite(course.endMin)) {
    return `${formatClockFromMin(course.startMin)} - ${formatClockFromMin(course.endMin)}`
  }
  if (Number.isFinite(course.startMin)) return formatClockFromMin(course.startMin)
  return course.startPeriod ? `${course.startPeriod}교시` : null
}

// ── 1. collectHeroSignals ────────────────────────────────────────────────
//
// 화면(App.jsx 등)이 이미 만들어둔 today 데이터를 받아서 정규화한다.
// localStorage/Firestore를 직접 읽지 않음 — 그래야 순수 함수로 유지되고
// 샘플 입력만으로 테스트할 수 있다.
//
// @param {object} input
//   now              {Date}    기준 시각 (기본 new Date())
//   uid              {string|null}
//   todayCourses     {Array}   오늘 요일로 이미 필터링된 시간표 (App.jsx의 getTodayCourses 결과와 동일 형태)
//   timetableExists  {boolean} 시간표 등록 여부 (App.jsx의 hasSavedTimetable 결과)
//   nextClassFuture  {object|null} 이번 주 내 가장 가까운 미래 수업 (getNextClassFutureDays 결과)
//   shuttle          {object|null} getEnhancedShuttleWithFavorites 결과 형태
//                       { isWeekend, isEnded, isFavorite, routeName, nextTime, firstTime, lastTime }
//   shuttleMinutesLeft {number|null}
//   cafeteriaSummary {object|null} getTodayCafeteriaSummary 결과 형태 { title, subtitle, meta, available }
//   hasCommuteSignal {boolean} 즐겨찾기 노선/기본 통학 노선 등 로컬 셔틀 데이터 존재 여부
export function collectHeroSignals(input = {}) {
  const {
    now = new Date(),
    uid = null,
    todayCourses = [],
    todayPersonalEvents = [],
    timetableExists = false,
    nextClassFuture = null,
    shuttle = null,
    shuttleMinutesLeft = null,
    cafeteriaSummary = null,
    hasCommuteSignal = false,
  } = input

  const nowMin = now.getHours() * 60 + now.getMinutes()
  const isWeekend = now.getDay() === 0 || now.getDay() === 6
  const courses = Array.isArray(todayCourses) ? todayCourses : []

  const withRange = courses
    .map(course => ({ course, startMin: getPeriodStartMin(course?.startPeriod), endMin: getPeriodEndMin(course?.endPeriod ?? course?.startPeriod) }))
    .filter(item => Number.isFinite(item.startMin) && Number.isFinite(item.endMin))

  const ongoingEntry = withRange.find(item => item.startMin <= nowMin && nowMin < item.endMin) ?? null
  const futureEntries = withRange
    .filter(item => item.startMin > nowMin)
    .sort((a, b) => a.startMin - b.startMin)
  const nextTodayEntry = futureEntries[0] ?? null
  const lastEntry = withRange.length > 0
    ? withRange.reduce((a, b) => (a.endMin >= b.endMin ? a : b))
    : null
  // 이미 끝난 수업 중 가장 최근에 끝난 것 — "쉬는 시간"/"공강" 판단에 쓰인다.
  // lastEntry(하루 중 가장 늦게 끝나는 수업, 시간 무관)와는 다른 개념이라 별도로 둔다.
  const previousEntry = withRange
    .filter(item => item.endMin <= nowMin)
    .sort((a, b) => b.endMin - a.endMin)[0] ?? null

  const toCourseSignal = entry => entry ? { ...entry.course, startMin: entry.startMin, endMin: entry.endMin } : null

  // ── 개인 일정 처리 (Course와 완전 분리된 신호) ──────────────────────────
  const peEntries = (Array.isArray(todayPersonalEvents) ? todayPersonalEvents : [])
    .map(event => {
      let startMin = event.startMin
      let endMin = event.endMin
      if (startMin == null && event.schedule?.startTime) {
        const [h, m] = event.schedule.startTime.split(':').map(Number)
        if (Number.isFinite(h) && Number.isFinite(m)) startMin = h * 60 + m
      }
      if (endMin == null && event.schedule?.endTime) {
        const [h, m] = event.schedule.endTime.split(':').map(Number)
        if (Number.isFinite(h) && Number.isFinite(m)) endMin = h * 60 + m
      }
      return {
        ...event,
        title: event.title || '개인 일정',
        name: event.title || '개인 일정',
        startMin,
        endMin,
      }
    })
    .filter(item => Number.isFinite(item.startMin) && Number.isFinite(item.endMin))

  const ongoingPEEntry = peEntries.find(item => item.startMin <= nowMin && nowMin < item.endMin) ?? null
  const futurePEEntries = peEntries
    .filter(item => item.startMin > nowMin)
    .sort((a, b) => a.startMin - b.startMin)
  const nextTodayPEEntry = futurePEEntries[0] ?? null

  const hasLiveShuttle = !!(shuttle && !shuttle.isWeekend && !shuttle.isEnded && shuttle.nextTime)

  return {
    now,
    nowMin,
    isWeekend,
    auth: {
      uid,
      isLoggedIn: !!uid,
    },
    timetable: {
      courses,
      hasAny: !!timetableExists,
    },
    ongoingClass: toCourseSignal(ongoingEntry),
    nextTodayClass: toCourseSignal(nextTodayEntry),
    lastTodayClass: toCourseSignal(lastEntry),
    previousTodayClass: toCourseSignal(previousEntry),
    ongoingPersonalEvent: ongoingPEEntry,
    nextTodayPersonalEvent: nextTodayPEEntry,
    todayPersonalEvents: peEntries,
    nextClassFuture,
    commute: {
      hasAny: !!hasCommuteSignal,
    },
    shuttle: {
      hasLiveShuttle,
      nextTime: shuttle?.nextTime ?? null,
      minsLeft: shuttleMinutesLeft ?? null,
      firstTime: shuttle?.firstTime ?? null,
      lastTime: shuttle?.lastTime ?? null,
      isEnded: !!shuttle?.isEnded,
      isWeekend: !!shuttle?.isWeekend,
      routeName: shuttle?.routeName ?? null,
      isFavorite: !!shuttle?.isFavorite,
    },
    cafeteria: {
      summary: cafeteriaSummary,
      available: !!cafeteriaSummary?.available,
    },
  }
}

// ── 2. computeTimeContext ────────────────────────────────────────────────
//
// 시계 시간만으로 결정되는 5구간. 요일(주말 여부)은 포함하지 않는다 —
// 주말 여부는 signals.isWeekend로 별도 전달되어 intent 계산에서만 쓰인다.
//
//   early-morning  00:00–06:00
//   morning        06:00–11:00
//   day            11:00–17:30
//   evening        17:30–20:00
//   night          20:00–24:00
export function computeTimeContext(signals) {
  const nowMin = signals?.nowMin ?? 0
  if (nowMin < 360) return 'early-morning'
  if (nowMin < 660) return 'morning'
  if (nowMin < 1050) return 'day'
  if (nowMin < 1200) return 'evening'
  return 'night'
}

// ── 3. computeDataConfidence ─────────────────────────────────────────────
//
//   full    로그인 + 시간표 있음 → 오늘 일정을 신뢰 가능
//   partial 로그인했지만 시간표 없음 / 비로그인이지만 즐겨찾기·기본노선 있음
//   empty   비로그인 + 데이터 전혀 없음
export function computeDataConfidence(signals) {
  const isLoggedIn = !!signals?.auth?.isLoggedIn
  const hasTimetable = !!signals?.timetable?.hasAny
  const hasCommuteSignal = !!signals?.commute?.hasAny

  if (isLoggedIn && hasTimetable) return 'full'
  if (isLoggedIn) return 'partial'
  if (hasCommuteSignal) return 'partial'
  return 'empty'
}

// ── 4. computeHeroIntent ─────────────────────────────────────────────────
//
// 우선순위 체인 (위에서부터 첫 매치 채택). 설계 문서의 P0~P7과 동일하되,
// morning/early-morning 경계와 evening/night 통합은 기존 getHomeHeroState
// 동작과의 비교를 위해 그 임계값(06/11/17:30/20시)을 그대로 따른다.
const LUNCH_START_MIN = 11 * 60        // 11:00
const LUNCH_END_MIN = 13 * 60 + 30     // 13:30
const BREAK_AFTER_PREV_THRESHOLD_MIN = 30 // 이전 수업 종료 후 이 안이면 "쉬는 시간", 넘으면 "공강"
const COMMUTE_WINDOW_END_MIN = 9 * 60  // 09:00 — 기존 getHomeHeroState의 등교 윈도우 상한과 동일

export function computeHeroIntent(signals, timeContext, dataConfidence) {
  const isFull = dataConfidence === 'full'
  const {
    ongoingClass, nextTodayClass, previousTodayClass,
    ongoingPersonalEvent, nextTodayPersonalEvent, todayPersonalEvents = [],
    shuttle, cafeteria, nowMin, isWeekend, timetable, commute
  } = signals ?? {}

  // 1. 현재 진행 중 수업 (사례 D: 수업과 개인 일정이 동시에 진행 중이면 수업이 main Hero)
  if (isFull && ongoingClass) {
    return { intent: 'in-class', reason: 'ongoing-class' }
  }

  // 1.5. 수업은 없지만 현재 개인 일정이 진행 중인 경우
  if (ongoingPersonalEvent) {
    return { intent: 'in-personal-event', reason: 'ongoing-personal-event' }
  }

  const classMins = (isFull && nextTodayClass) ? nextTodayClass.startMin - nowMin : Infinity
  const peMins = nextTodayPersonalEvent ? nextTodayPersonalEvent.startMin - nowMin : Infinity

  function resolveClassReason() {
    const timingState = resolveClassTimingState({
      nowMin,
      startMin: nextTodayClass.startMin,
      endMin: nextTodayClass.endMin,
      hasPreviousClass: !!previousTodayClass,
    })
    const withinCountdownWindow = timingState === HOME_CLASS_TIMING_STATES.IMMINENT || timingState === HOME_CLASS_TIMING_STATES.COUNTDOWN
    if (withinCountdownWindow) {
      if (!previousTodayClass) {
        return 'first-class'
      }
      const gapAfterPrev = nowMin - previousTodayClass.endMin
      return gapAfterPrev <= BREAK_AFTER_PREV_THRESHOLD_MIN ? 'break' : 'gap'
    }
    return timingState === HOME_CLASS_TIMING_STATES.FAR_FIRST ? 'far-first' : 'far-next'
  }

  // 2. 15분 이내 수업
  if (isFull && nextTodayClass && classMins <= HOME_CLASS_IMMINENT_MAX_MINUTES) {
    return { intent: 'before-class', reason: resolveClassReason() }
  }

  // 3. 15분 이내 개인 일정
  if (nextTodayPersonalEvent && peMins <= HOME_CLASS_IMMINENT_MAX_MINUTES) {
    return { intent: 'before-personal-event', reason: 'imminent-personal-event' }
  }

  // 4. 기존 등교/셔틀 등 중요한 학교 context (06:00–09:00)
  if (timeContext === 'morning' && nowMin < COMMUTE_WINDOW_END_MIN) {
    const hasCommuteCardSignal = !!shuttle?.hasLiveShuttle || !!commute?.hasAny
    if (isFull && nextTodayClass && hasCommuteCardSignal) {
      return { intent: 'commute-to-school', reason: 'class-ahead' }
    }
    if (!isFull && hasCommuteCardSignal) {
      return { intent: 'commute-to-school', reason: 'generic-morning-shuttle' }
    }
  }

  // 5. 120분 이내의 가장 가까운 occurrence (수업/개인 일정 모두 후보, 같은 시간이면 수업 우선)
  const hasClassWithin120 = isFull && nextTodayClass && classMins <= HOME_CLASS_COUNTDOWN_MAX_MINUTES
  const hasPEWithin120 = nextTodayPersonalEvent && peMins <= HOME_CLASS_COUNTDOWN_MAX_MINUTES

  if (hasClassWithin120 && hasPEWithin120) {
    if (classMins <= peMins) {
      return { intent: 'before-class', reason: resolveClassReason() }
    } else {
      return { intent: 'before-personal-event', reason: 'countdown-personal-event' }
    }
  }

  if (hasPEWithin120 && !hasClassWithin120) {
    // 120분 이내 개인 일정만 존재 (사례 A: 17:40 수업 끝, 18:00 알바 / 사례 E: 수업 없고 18:00 알바)
    return { intent: 'before-personal-event', reason: 'countdown-personal-event' }
  }

  if (hasClassWithin120 && !hasPEWithin120) {
    return { intent: 'before-class', reason: resolveClassReason() }
  }

  // 6. 점심 (학식 운영 데이터가 있거나, 남은 수업이 없을 때)
  if (timeContext === 'day' && nowMin >= LUNCH_START_MIN && nowMin < LUNCH_END_MIN && (cafeteria?.available || !nextTodayClass)) {
    return { intent: 'lunch', reason: cafeteria?.available ? 'lunch-window' : 'lunch-window-no-data' }
  }

  // 7. 120분 넘게 남은 수업 (기존 Hero 정책 유지)
  if (isFull && nextTodayClass) {
    return { intent: 'before-class', reason: resolveClassReason() }
  }

  // 8. 수업이 모두 끝난 뒤 / 저녁
  // (사례 A: 120분 이내 개인 일정은 위 5번에서 이미 처리됨)
  // (사례 B: 수업 종료 후 5시간 뒤 알바 -> 아래 day-done/go-home 유지하고 보조 정보로 표시)
  if ((timeContext === 'evening' || timeContext === 'night') && !nextTodayClass) {
    return shuttle?.hasLiveShuttle
      ? { intent: 'go-home', reason: isFull ? 'classes-done-evening' : 'generic-evening-shuttle' }
      : { intent: 'day-done', reason: isFull ? 'today-finished' : 'generic-evening-done' }
  }

  // 8.5 일반 셔틀 폴백
  if (shuttle?.hasLiveShuttle) {
    return { intent: 'shuttle-info', reason: 'generic-live-shuttle' }
  }

  // 8.7 오늘 수업은 없지만 오늘 개인 일정이 남아있는 경우 (사례 E: 120분 초과하여 남은 개인 일정)
  if (nextTodayPersonalEvent) {
    return { intent: 'personal-event-far', reason: 'today-personal-event-ahead' }
  }

  // 8.8 주말 / 휴식일 (수업도 없고 개인 일정도 없는 경우만)
  if (isWeekend && (timetable?.courses?.length ?? 0) === 0 && todayPersonalEvents.length === 0) {
    return { intent: 'rest-day', reason: 'weekend-no-class' }
  }
  if (isFull && (timetable?.courses?.length ?? 0) === 0 && todayPersonalEvents.length === 0) {
    return { intent: 'rest-day', reason: isWeekend ? 'weekend-no-class' : 'weekday-no-class' }
  }

  // 9. 사용자 일정을 모름 (최후 폴백)
  return { intent: 'empty-timetable', reason: `confidence-${dataConfidence}-no-signal` }
}

// ── 5. buildHomeHeroViewModel ─────────────────────────────────────────────
//
// 기존 getHomeHeroState() 반환 형태(type/label/title/subtitle/meta/
// remainingMin/context/tone/action)와, App.jsx가 별도로 넘기던
// heroShuttleRaw({ nextTime, minsLeft } → shuttleTime/minsToShuttle)를
// 하나의 객체로 합쳐서 반환한다 — 화면에 연결할 때 prop 매핑 비용을 줄이기 위함.
//
// @param {object} heroState  { intent, reason, timeContext, dataConfidence }
// @param {object} signals    collectHeroSignals() 결과
export function buildHomeHeroViewModel(heroState, signals) {
  const { intent, reason, timeContext, dataConfidence } = heroState ?? {}
  const {
    ongoingClass, nextTodayClass, nextClassFuture, shuttle, cafeteria, timetable, nowMin,
    ongoingPersonalEvent, nextTodayPersonalEvent, todayPersonalEvents = [],
  } = signals ?? {}

  const base = {
    // 디버그/QA용 raw 상태 — 기존 hero 객체엔 없던 필드, 화면에선 무시해도 됨
    intent, reason, timeContext, dataConfidence,
    // 기존 hero 객체와 동일한 모양
    type: 'default',
    label: '',
    title: '',
    subtitle: '',
    meta: null,
    remainingMin: null,
    context: null,
    tone: 'surface',
    action: 'timetable',
    room: null,
    // 기존 heroShuttleRaw(shuttleTime/minsToShuttle)와 동일한 모양
    shuttleTime: null,
    minsToShuttle: null,
  }

  switch (intent) {
    case 'in-class': {
      const remainingMin = ongoingClass ? Math.max(0, ongoingClass.endMin - nowMin) : null
      let subtitle = ongoingClass?.room || '강의실 미정'
      if (ongoingPersonalEvent) {
        subtitle = `${subtitle} (일정 '${ongoingPersonalEvent.title}' 겹침)`
      }
      return {
        ...base,
        type: 'class-ongoing',
        label: '수업 중',
        title: ongoingClass?.name ?? ongoingClass?.courseName ?? '수업',
        subtitle,
        meta: getClassTimeLabel(ongoingClass),
        room: ongoingClass?.room ?? null,
        remainingMin,
        timingState: HOME_CLASS_TIMING_STATES.ACTIVE,
        tone: 'brand',
        action: 'timetable',
      }
    }

    case 'in-personal-event': {
      const remainingMin = ongoingPersonalEvent ? Math.max(0, ongoingPersonalEvent.endMin - nowMin) : null
      const startStr = formatClockFromMin(ongoingPersonalEvent?.startMin)
      const endStr = formatClockFromMin(ongoingPersonalEvent?.endMin)
      return {
        ...base,
        type: 'personal-ongoing',
        label: '일정 진행 중',
        title: ongoingPersonalEvent?.title ?? '개인 일정',
        subtitle: `${startStr} - ${endStr}`,
        meta: nextTodayClass ? `다음 수업: ${nextTodayClass.name}` : '개인 일정',
        remainingMin,
        tone: 'surface',
        action: 'timetable',
      }
    }

    case 'before-personal-event': {
      const pe = nextTodayPersonalEvent
      const remainingMin = pe ? Math.max(0, pe.startMin - nowMin) : null
      const isSoon = remainingMin != null && remainingMin <= HOME_CLASS_IMMINENT_MAX_MINUTES
      const startStr = formatClockFromMin(pe?.startMin)
      return {
        ...base,
        type: isSoon ? 'personal-soon' : 'personal-upcoming',
        label: isSoon ? '곧 일정' : '다음 일정',
        title: pe?.title ?? '개인 일정',
        subtitle: `${startStr} · ${formatRelativeTime(remainingMin)}`,
        meta: nextTodayClass ? `다음 수업: ${nextTodayClass.name}` : (isSoon ? '곧 시작돼요' : '시간표 ›'),
        remainingMin,
        tone: 'surface',
        action: 'timetable',
      }
    }

    case 'personal-event-far': {
      const pe = nextTodayPersonalEvent
      const startStr = formatClockFromMin(pe?.startMin)
      return {
        ...base,
        type: 'default',
        label: '오늘 일정',
        title: `${startStr}에 ${pe?.title ?? '일정'}이 있어요`,
        subtitle: signals?.isWeekend ? '여유로운 주말을 보내세요' : '오늘은 수업이 없어요',
        meta: '시간표 ›',
        tone: 'surface',
        action: 'timetable',
      }
    }

    case 'before-class': {
      const course = nextTodayClass
      const remainingMin = course ? Math.max(0, course.startMin - nowMin) : null
      const isSoon = remainingMin != null && remainingMin <= HOME_CLASS_IMMINENT_MAX_MINUTES
      const timingState = reason === 'far-first'
        ? HOME_CLASS_TIMING_STATES.FAR_FIRST
        : reason === 'far-next'
          ? HOME_CLASS_TIMING_STATES.FAR_NEXT
          : isSoon
            ? HOME_CLASS_TIMING_STATES.IMMINENT
            : HOME_CLASS_TIMING_STATES.COUNTDOWN
      const labelByReason = { 'far-first': '첫 일정', 'far-next': '다음 수업', 'first-class': '첫 일정', break: '쉬는 시간', gap: '공강' }
      return {
        ...base,
        type: isSoon ? 'class-upcoming' : 'class-next',
        label: labelByReason[reason] ?? (isSoon ? '곧 수업' : '다음 수업'),
        title: course?.name ?? course?.courseName ?? '다음 수업',
        subtitle: course?.room || '강의실 미정',
        meta: getClassTimeLabel(course),
        room: course?.room ?? null,
        remainingMin,
        timingState,
        context: ['first-class', 'break', 'gap'].includes(reason) ? reason : null,
        tone: 'surface',
        action: 'timetable',
      }
    }

    case 'lunch': {
      return {
        ...base,
        type: 'meal',
        label: '오늘 학식',
        title: cafeteria?.summary?.title ?? '점심 시간이에요',
        subtitle: cafeteria?.summary?.subtitle ?? '학식 운영 중',
        meta: cafeteria?.summary?.meta ?? null,
        tone: 'surface',
        action: 'cafeteria',
      }
    }

    case 'commute-to-school':
    case 'go-home': {
      const isGoHome = intent === 'go-home'
      return {
        ...base,
        type: 'shuttle',
        label: isGoHome ? '귀가 셔틀' : '등교 셔틀',
        title: shuttle?.nextTime ? `${shuttle.nextTime} 출발` : '셔틀 시간 확인하기',
        subtitle: shuttle?.routeName ?? '셔틀 안내',
        meta: shuttle?.minsLeft != null ? formatRelativeTime(shuttle.minsLeft) : '학기 중 평일 운행 기준',
        remainingMin: shuttle?.minsLeft ?? null,
        context: isGoHome ? 'go-home' : 'commute',
        tone: 'surface',
        action: 'shuttle',
        shuttleTime: shuttle?.nextTime ?? null,
        minsToShuttle: shuttle?.minsLeft ?? null,
      }
    }

    // 등교/하교 시간창 밖에서도 실시간 셔틀이 있으면 보여주는 일반 폴백 —
    // 기존 getHomeHeroState 맨 끝의 "셔틀 안내" 카드와 동일한 모양.
    case 'shuttle-info': {
      return {
        ...base,
        type: 'shuttle',
        label: '셔틀 안내',
        title: shuttle?.nextTime ? `${shuttle.nextTime} 출발` : '셔틀 시간 확인하기',
        subtitle: shuttle?.routeName ?? '셔틀 안내',
        meta: shuttle?.minsLeft != null ? formatRelativeTime(shuttle.minsLeft) : '학기 중 평일 운행 기준',
        remainingMin: shuttle?.minsLeft ?? null,
        context: timeContext === 'early-morning' ? 'early' : null,
        tone: 'surface',
        action: 'shuttle',
        shuttleTime: shuttle?.nextTime ?? null,
        minsToShuttle: shuttle?.minsLeft ?? null,
      }
    }

    case 'day-done': {
      const nextInfo = nextTodayPersonalEvent
        ? `오늘 ${formatClockFromMin(nextTodayPersonalEvent.startMin)} ${nextTodayPersonalEvent.title} 예정`
        : (nextClassFuture ? `${nextClassFuture.dayLabel} · ${nextClassFuture.timeStr}` : null)
      return {
        ...base,
        type: 'default',
        label: '오늘 수업 마무리',
        title: '오늘 하루 수고했어요',
        subtitle: nextTodayPersonalEvent
          ? `${formatClockFromMin(nextTodayPersonalEvent.startMin)}에 ${nextTodayPersonalEvent.title} 일정이 있어요`
          : (nextClassFuture
              ? `다음 수업은 ${nextClassFuture.dayLabel} ${nextClassFuture.timeStr?.split(' - ')[0] ?? ''}이에요`
              : '내일 일정을 미리 확인해보세요'),
        meta: nextInfo ?? '시간표 ›',
        context: 'evening-done',
        tone: 'surface',
        action: 'timetable',
      }
    }

    case 'rest-day': {
      return {
        ...base,
        type: 'weekend-empty',
        label: '오늘 수업이 없어요',
        title: '오늘은 수업이 없어요',
        subtitle: '여유로운 주말을 보내세요',
        meta: '시간표 ›',
        tone: 'surface',
        action: 'timetable',
      }
    }

    case 'empty-timetable':
    default: {
      const hasTimetable = !!timetable?.hasAny
      return {
        ...base,
        type: 'default',
        label: hasTimetable ? '오늘 남은 수업 없음' : '오늘도 화이팅!',
        title: hasTimetable ? '오늘 수업은 마무리됐어요' : '시간표를 등록하면 다음 수업을 보여드려요',
        subtitle: hasTimetable ? '필요하면 시간표에서 이번 주 수업을 확인하세요' : '수업, 셔틀, 학식을 홈에서 바로 확인할 수 있어요',
        meta: hasTimetable ? '시간표 ›' : '시간표 등록하기 ›',
        tone: 'surface',
        action: 'timetable',
      }
    }
  }
}

// ── 비교/검증용 샘플 (화면에 연결되지 않음, 데이터+조립 헬퍼만 제공) ─────────────
//
// 사용 예 (브라우저/노드 콘솔에서):
//   import { HERO_STATE_ENGINE_SAMPLES, runHeroStateEngine } from './heroStateEngine'
//   HERO_STATE_ENGINE_SAMPLES.forEach(c => console.log(c.label, runHeroStateEngine(c.input)))

export function runHeroStateEngine(input) {
  const signals = collectHeroSignals(input)
  const timeContext = computeTimeContext(signals)
  const dataConfidence = computeDataConfidence(signals)
  const { intent, reason } = computeHeroIntent(signals, timeContext, dataConfidence)
  const heroState = { intent, reason, timeContext, dataConfidence }
  return buildHomeHeroViewModel(heroState, signals)
}

function atHour(h, m = 0) {
  const d = new Date(2026, 0, 13, h, m, 0) // 2026-01-13 화요일 (평일)
  return d
}

export const HERO_STATE_ENGINE_SAMPLES = [
  {
    label: '1) 시간표 없음 + 아침 (비로그인, 즐겨찾기/기본노선도 없음)',
    input: {
      now: atHour(7, 30),
      uid: null,
      todayCourses: [],
      timetableExists: false,
      nextClassFuture: null,
      shuttle: null,
      shuttleMinutesLeft: null,
      cafeteriaSummary: null,
      hasCommuteSignal: false,
    },
  },
  {
    label: '2) 수업 중 (10:00 수업, 09:30~10:20)',
    input: {
      now: atHour(10, 0),
      uid: 'user-1',
      todayCourses: [
        { name: '자료구조', room: '공학관 401', startPeriod: 1, endPeriod: 1 },
      ],
      timetableExists: true,
      nextClassFuture: null,
      shuttle: null,
      shuttleMinutesLeft: null,
      cafeteriaSummary: null,
      hasCommuteSignal: false,
    },
  },
  {
    label: '3) 다음 수업 30분 전 (10:30 수업, 현재 10:00)',
    input: {
      now: atHour(10, 0),
      uid: 'user-1',
      todayCourses: [
        { name: '알고리즘', room: '공학관 305', startPeriod: 2, endPeriod: 2 },
      ],
      timetableExists: true,
      nextClassFuture: null,
      shuttle: null,
      shuttleMinutesLeft: null,
      cafeteriaSummary: null,
      hasCommuteSignal: false,
    },
  },
  {
    label: '4) 점심 시간 (12:10, 오늘 수업은 있지만 지금은 공강)',
    input: {
      now: atHour(12, 10),
      uid: 'user-1',
      todayCourses: [
        { name: '오전 수업', room: '인문관 201', startPeriod: 1, endPeriod: 1 },
        { name: '오후 수업', room: '인문관 202', startPeriod: 6, endPeriod: 6 },
      ],
      timetableExists: true,
      nextClassFuture: null,
      shuttle: null,
      shuttleMinutesLeft: null,
      cafeteriaSummary: { title: '제육볶음, 계란국', subtitle: '학생식당', meta: '11:00 업데이트', available: true },
      hasCommuteSignal: false,
    },
  },
  {
    label: '5) 저녁 하교 (18:30, 오늘 수업 끝남 + 다음 셔틀 18:40)',
    input: {
      now: atHour(18, 30),
      uid: 'user-1',
      todayCourses: [
        { name: '오전 수업', room: '인문관 201', startPeriod: 1, endPeriod: 1 },
      ],
      timetableExists: true,
      nextClassFuture: null,
      shuttle: { isWeekend: false, isEnded: false, isFavorite: true, routeName: '학교 → 병점역', nextTime: '18:40', firstTime: '08:30', lastTime: '22:00' },
      shuttleMinutesLeft: 10,
      cafeteriaSummary: null,
      hasCommuteSignal: true,
    },
  },
  {
    label: '6) 등교 윈도우인데 셔틀 데이터 없음 (08:30, 09:30 수업) → before-class여야 함',
    input: {
      now: atHour(8, 30),
      uid: 'user-1',
      todayCourses: [
        { name: '전공수업', room: '공학관 401', startPeriod: 1, endPeriod: 1 },
      ],
      timetableExists: true,
      nextClassFuture: null,
      shuttle: null,
      shuttleMinutesLeft: null,
      cafeteriaSummary: null,
      hasCommuteSignal: false,
    },
  },
]
